<template>
  <section>
    <one-line-form :submit="updateOpenQuestion" :submittable="!!openQuestion && isValid && !duplicated" :saving="isSaving ? openQuestion : ''">
        <input-field name="openQuestion"
                   :class="{ invisible: isSaving }"
                   type="text"
                   :maxlength="(screenParams.maxLength && screenParams.maxLength.toString()) || ''"
                   :title="screenParams.title()"
                   :placeholder="screenParams.placeholder()"
                   :autocapitalize="true"
                   :autocorrect="false"
                   :value="openQuestion"
                   @input="syncOpenQuestion" />
      <p 
       class="hint countryprompt" v-show="!isSaving" @click="showCountryModal = true">{{ getLocalCountryName(selectedCountry, i18n) }}</p>
    </one-line-form>
    <helper v-if="duplicated && !isSaving" :error="true" class="warningHint">{{ screenParams.duplicated() }}</helper>
    <helper v-if="!duplicated && screenParams.description">
      <p v-show="!isSaving">{{ screenParams.description($store) }}</p>
    </helper>
    <search-selector
      v-if="showCountryModal"
      :options="getCountriesOptions"
      @submit="selectCountry"
      :has-other="false"
      :placeholder="i18n.t('country:search')"
      @close="showCountryModal = false"
      :is-overlay="true"
    />
    <show-keyboard></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  .hidden-input {
    top: -1000px;
    position: absolute;
  }

  /deep/ .list-container {
    @import '../assets/country-flags';

    .flag,
    .flag.flag-icon-squared {
      display: inline-block;
      background-repeat: no-repeat;
      width:24px !important;
      height: 24px !important;
      vertical-align: super;
      margin-right: 14px;
      top: 2px;
      border-radius: 50%;
      border: solid 1px #EDEDED;
    }
  }

  .warningHint {
    margin-bottom: 10px;
  }
  .countryprompt{
    &::after{
      content: ' ';
      background-image: url('~static/dropdown.svg');
      background-repeat: no-repeat;
      height: 6px;
      width: 10px;
      position: relative;
      display: inline-block;
      margin-left: 8px;
    }
    &.disabled::after{
      display: none;
    }
  }
</style>

<script>
  import Vue from 'vue'
  import { isValid as isValidEmail } from '../utils/email'
  import { mapState } from 'vuex'
  import config from '../partners/partner.module.config'

  import searchSelector from 'components/SearchSelector'

  export default Vue.component('open-question-screen', {
    props: ['screenParams'],
    components: {
      'search-selector': searchSelector
    },
    data () {
      const state = this.$store.state
      const selectedCountry = (state.user.birthplace && state.user.birthplace.country) || state.app.country.code

      return {
        openQuestion: this.screenParams.currentAnswer(this.$store) || '',
        isSaving: false,
        showCountryModal: false,
        selectedCountry
      }
    },
    computed: {
      ...mapState(['partner', 'l10n', 'location', 'app']),
      isValid () {
        if(this.screenParams.minLength && this.openQuestion?.length < this.screenParams.minLength) {
          return false
        }
        else if (!this.screenParams.validation) {
          return true
        } else if (this.screenParams.validation.validator) {
          return this.screenParams.validation.validator(this.openQuestion)
        } else {
          switch (this.screenParams.validation.type) {
            case 'email' :
              return isValidEmail(this.openQuestion)
            case 'phone' :
              return this.validatePhone(this.openQuestion)
            case 'fullName' :
              return this.validateFullName(this.openQuestion)
            case 'placeOfBirth' :
              return this.validatePlaceOfBirth(this.openQuestion)
            default:
              return true
          }
        }
      },
      duplicated () {
        if (this.screenParams.validation && this.screenParams.validation.duplicateCheck) {
          const originalValue = this.retrieveValue(this.screenParams.validation.duplicateValue, this.$store.state)
          return this.openQuestion && originalValue && originalValue === this.openQuestion
        } else {
          return false
        }
      },
      countries () {
        let filteredCountries = []

        for (let countryCode of Object.keys(this.$store.state.l10n)) {
            // Maps country code instead of loading every country, because the selecotr uses only country code
            let country = !!this.l10n[countryCode] && {
              code: countryCode,
              name: this.getLocalCountryName(countryCode, this.i18n)
            }
            let isFeaturedCountry = this.partner.featuredCountries.indexOf(countryCode) !== -1

            if (country && country !== null && country !== undefined && !isFeaturedCountry) filteredCountries.push(country)
        }

        return filteredCountries.sort(this.sortAlphebetically)
      },
      topCountries () {
        let filteredCountries = []

        for (let countryCode of this.partner.featuredCountries) {
          let isRestricted = this.isRestrictedCountry(countryCode)

          if (!isRestricted) {
            let country = !!this.l10n[countryCode] && {
              code: countryCode
            }

            if (country && country !== null && country !== undefined && country.code === this.location.countryCode) {
              filteredCountries.unshift(country)
            } else if (country && country !== null && country !== undefined) {
              filteredCountries.push(country)
            }
          }
        }

        return filteredCountries
      },
      getCountriesOptions () {
        let countries = this.countries
        let topCountries = this.topCountries.map((country) => {
          country.prepend = `<span class="flag flag-icon flag-icon-squared flag-icon-${country.code.toLowerCase()}"></span>`
          country.name = this.getLocalCountryName(country.code, this.i18n)
          country.hideBorders = true
          return country
        })

        const groupSeparator = this.i18n.t('country:group_separator_all')
        if (groupSeparator) {
          topCountries.push({
            subheader: groupSeparator
          })
        }

        return topCountries.concat(countries).map((country) => {
            return {
              label: country.name,
              value: country.code,
              prepend: country.prepend || null,
              hideBorders: country.hideBorders || null,
              subheader: country.subheader || null
            }
        })
      }
    },
    methods: {
      validateFullName (name) {
        let splitFullname = name.split(' ')
        return (splitFullname.length >= 2 && !!splitFullname[1])
      },
      validatePhone (phone) {
        return /^\d{5,}$/.test(phone)
      },
      syncOpenQuestion (value) {
        if (this.usingPrefill && value !== this.openQuestion) this.usingPrefill = false
        this.openQuestion = value
      },
      updateOpenQuestion () {
        if (this.openQuestion) {
          this.isSaving = true
          let answerData = {}
          if (this.screenParams.isNested && this.screenParams.nestedProperty) {
            answerData[this.screenParams.propertyName] = {
              [this.screenParams.nestedProperty]: this.openQuestion,
              country: this.selectedCountry
            }
          } else {
            answerData[this.screenParams.propertyName] = this.openQuestion
          }
          this.$store.dispatch(this.screenParams.dispatchKey, answerData)
          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }
      },
      retrieveValue (path, obj, separator = '.') {
        const properties = Array.isArray(path) ? path : path.split(separator)
        return properties.reduce((prev, curr) => prev && prev[curr], obj)
      },
      isRestrictedCountry (countryCode) {
        return this.partner.restrictedCountries ? this.partner.restrictedCountries.indexOf(countryCode) !== -1 : false
      },
      selectCountry (country) {
        this.showCountryModal = false
        this.selectedCountry = country.value
      },
      validatePlaceOfBirth (place) {
        const {placeOfBirthRegexExp} =  config.settings.customRegex;
        return new RegExp(placeOfBirthRegexExp).test(place)
      }
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/BirthPlace.vue